import useGetAvatar from '@/hooks/useGetAvatar';
import useGetAvatarProperties from '@/hooks/useGetAvatarProperties';
import useGetGroupAvatars from '@/hooks/useGetGroupAvatars';
import { composeUri } from '@/utils/compose';
import {
    AVATAR_GROUP_AUDIT_TYPE,
    AVATAR_GROUP_QUOTE_TYPE,
    AVATARS,
    INTERVENTION_STATUS_AVATAR_PROPERTY,
} from '@/utils/CONST';
import Box from '@mui/material/Box';
import { get, nth } from 'lodash';
import moment from 'moment/moment';
import { useTranslate } from 'react-admin';
import ExportLabelValue from '@/components/interventions/export-intervention/common/export-label-value';
import ExportSectionBox from '@/components/interventions/export-intervention/common/export-section-box';

interface ExportInterventionAdditionalSectionProps {
    interventionId: string;
}

const ExportInterventionAdditionalSection = ({ interventionId }: ExportInterventionAdditionalSectionProps) => {
    const translate = useTranslate();

    const { avatar: intervention, isLoading: isLoadingInt } = useGetAvatar({
        avatarUriId: composeUri(AVATARS, interventionId),
    });

    const { properties, isLoading: isLoadingProperties } = useGetAvatarProperties({
        avatarId: interventionId,
        propertiesNames: [INTERVENTION_STATUS_AVATAR_PROPERTY],
        enabled: !!intervention,
    });

    const property = nth(properties, 0);
    
    const duration =
        property && property.customFields?.time_passed > 0
            ? moment().locale('fr').subtract(property.customFields.time_passed, 'minutes').fromNow(true)
            : '---';

    const interventionGroupAvatar: any = nth(get(intervention, 'groupAvatars'), 0);

    const { groupAvatars: quotes, isLoading: isLoadingQuotes } = useGetGroupAvatars({
        filters: {
            id: interventionGroupAvatar,
            type: AVATAR_GROUP_QUOTE_TYPE,
        },
        enabled: !!interventionGroupAvatar,
    });

    const quoteName = get(nth(quotes, 0), 'name');

    const { groupAvatars: audits, isLoading: isLoadingAudits } = useGetGroupAvatars({
        filters: {
            id: interventionGroupAvatar,
            type: AVATAR_GROUP_AUDIT_TYPE,
        },
        enabled: !!interventionGroupAvatar,
    });

    const auditName = get(nth(audits, 0), 'name');

    if (isLoadingInt || isLoadingProperties || isLoadingQuotes || isLoadingAudits) {
        return null;
    }

    if (duration === '---' && !quoteName && !auditName) {
        return null;
    }

    return (
        <ExportSectionBox title={translate('intervention.export.section.additional')}>
            <Box
                sx={{
                    display: 'grid',
                    gap: 1,
                }}
            >
                {duration !== '---' && (
                    <ExportLabelValue label={translate('intervention.list.duration')} value={duration} />
                )}

                {quoteName && <ExportLabelValue label={translate('disorder.list.present_devis')} value={quoteName} />}

                {auditName && <ExportLabelValue label={translate('disorder.list.present_audit')} value={auditName} />}
            </Box>
        </ExportSectionBox>
    );
};

export default ExportInterventionAdditionalSection;
